import { useState, useEffect } from "react";
import { Users, Image, Phone, Mail, MapPin, Clock, Search, Trash2, Eye, FileText, ChevronDown, ChevronRight, LogIn, LogOut, ArrowRight } from "lucide-react";
import { BUSINESS } from "@/data/businessInfo";
import { SEO } from "@/components/SEO";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = ({ onLogin }) => {
  const [pass, setPass] = useState("");
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-8 w-full max-w-sm shadow-lg">
        <img src={BUSINESS.logo} alt={BUSINESS.name} className="h-16 mx-auto mb-6" />
        <h1 className="text-xl font-bold text-[#401303] text-center mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Panel</h1>
        <form onSubmit={e => { e.preventDefault(); onLogin(pass); }}>
          <input type="password" value={pass} onChange={e => setPass(e.target.value)} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm mb-4" placeholder="Enter admin password" data-testid="admin-password" />
          <button type="submit" className="w-full btn-gradient rounded-full py-3 text-sm font-bold" data-testid="admin-login-btn">
            <LogIn className="w-4 h-4 inline mr-2" /> Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

const LeadCard = ({ lead, onUpdateStatus }) => {
  const statusColors = { pending: "bg-yellow-100 text-yellow-800", contacted: "bg-blue-100 text-blue-800", converted: "bg-green-100 text-green-800", closed: "bg-gray-100 text-gray-600" };
  return (
    <div className="bg-white rounded-xl border border-[#6B3A1F]/10 p-5 hover:shadow-md transition-shadow duration-200" data-testid={`lead-${lead.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-bold text-[#401303] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{lead.name}</p>
          <p className="text-xs text-[#6B3A1F]">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : 'Recent'}</p>
        </div>
        <select value={lead.status || 'pending'} onChange={e => onUpdateStatus(lead.id, e.target.value)} className={`text-xs font-semibold px-2 py-1 rounded-full border-0 ${statusColors[lead.status] || statusColors.pending}`}>
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </select>
      </div>
      <div className="space-y-1.5 text-xs text-[#6B3A1F]">
        {lead.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-[#D97904]" /><a href={`tel:${lead.phone}`} className="hover:text-[#D97904]">{lead.phone}</a></div>}
        {lead.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-[#D97904]" /><a href={`mailto:${lead.email}`} className="hover:text-[#D97904]">{lead.email}</a></div>}
        {lead.service && <div className="flex items-center gap-2"><FileText className="w-3 h-3 text-[#D97904]" /><span>{lead.service}</span></div>}
        {lead.city && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#D97904]" /><span>{lead.city}</span></div>}
        {lead.message && <p className="mt-2 pt-2 border-t border-[#6B3A1F]/10 text-[#6B3A1F]">{lead.message}</p>}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const handleLogin = (pass) => {
    if (pass === "sls2024admin") { setAuthed(true); toast.success("Welcome, Admin!"); }
    else toast.error("Incorrect password");
  };

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/contacts`).then(r => setLeads(r.data.reverse())).catch(() => {});
    axios.get(`${API}/bookings`).then(r => setBookings(r.data.reverse())).catch(() => {});
  }, [authed]);

  const updateLeadStatus = async (id, status) => {
    try {
      await axios.put(`${API}/contacts/${id}/status`, { status });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
      toast.success(`Lead marked as ${status}`);
    } catch {
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}/status`, { status });
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
      toast.success(`Booking marked as ${status}`);
    } catch {
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    }
  };

  if (!authed) return <AdminLogin onLogin={handleLogin} />;

  const filteredLeads = leads.filter(l => {
    const matchSearch = !filter || [l.name, l.email, l.phone, l.service, l.city, l.message].some(f => f?.toLowerCase().includes(filter.toLowerCase()));
    const matchStatus = statusFilter === "all" || (l.status || "pending") === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredBookings = bookings.filter(b => {
    const matchSearch = !filter || [b.name, b.email, b.phone, b.service, b.city].some(f => f?.toLowerCase().includes(filter.toLowerCase()));
    const matchStatus = statusFilter === "all" || b.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = {
    totalLeads: leads.length + bookings.length,
    pending: leads.filter(l => !l.status || l.status === 'pending').length + bookings.filter(b => b.status === 'pending').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    converted: leads.filter(l => l.status === 'converted').length,
  };

  const TABS = [
    { id: "leads", label: "Contact Leads", icon: Users, count: leads.length },
    { id: "bookings", label: "Bookings", icon: Clock, count: bookings.length },
    { id: "photos", label: "Photo Manager", icon: Image, count: BUSINESS.gallery?.length || 0 },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEO title="Admin Panel" path="/admin" />
      {/* Admin Header */}
      <div className="bg-[#401303] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BUSINESS.logo} alt="" className="h-10" />
            <div>
              <p className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Dashboard</p>
              <p className="text-xs text-white/60">{BUSINESS.name}</p>
            </div>
          </div>
          <button onClick={() => setAuthed(false)} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors duration-200">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Leads", value: stats.totalLeads, color: "text-[#401303]" },
            { label: "Pending", value: stats.pending, color: "text-yellow-600" },
            { label: "Contacted", value: stats.contacted, color: "text-blue-600" },
            { label: "Converted", value: stats.converted, color: "text-green-600" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#6B3A1F]/10 p-4 text-center">
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</p>
              <p className="text-xs text-[#6B3A1F]">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${tab === t.id ? 'bg-[#D97904] text-white' : 'bg-white border border-[#6B3A1F]/15 text-[#401303] hover:border-[#D97904]'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid={`admin-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label} <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs">{t.count}</span>
            </button>
          ))}
        </div>

        {/* Search & Filter */}
        {tab !== "photos" && (
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B3A1F]/40" />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm bg-white" placeholder="Search leads..." data-testid="admin-search" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2.5 border-2 border-[#6B3A1F]/15 rounded-xl focus:border-[#D97904] focus:outline-none text-sm bg-white" data-testid="admin-status-filter">
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        )}

        {/* Leads Tab */}
        {tab === "leads" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-leads-grid">
            {filteredLeads.length === 0 && <p className="col-span-full text-center text-[#6B3A1F] py-12">No leads yet. They'll appear here when visitors submit the contact form.</p>}
            {filteredLeads.map(lead => <LeadCard key={lead.id} lead={lead} onUpdateStatus={updateLeadStatus} />)}
          </div>
        )}

        {/* Bookings Tab */}
        {tab === "bookings" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-bookings-grid">
            {filteredBookings.length === 0 && <p className="col-span-full text-center text-[#6B3A1F] py-12">No bookings yet.</p>}
            {filteredBookings.map(booking => (
              <div key={booking.id} className="bg-white rounded-xl border border-[#6B3A1F]/10 p-5 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-[#401303] text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{booking.name}</p>
                    <p className="text-xs text-[#6B3A1F]">{booking.service} ({booking.service_type})</p>
                  </div>
                  <select value={booking.status} onChange={e => updateBookingStatus(booking.id, e.target.value)} className="text-xs font-semibold px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 border-0">
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="space-y-1.5 text-xs text-[#6B3A1F]">
                  {booking.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-[#D97904]" /><a href={`tel:${booking.phone}`} className="hover:text-[#D97904]">{booking.phone}</a></div>}
                  {booking.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-[#D97904]" />{booking.email}</div>}
                  {booking.city && <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#D97904]" />{booking.city}</div>}
                  {booking.preferred_date && <div className="flex items-center gap-2"><Clock className="w-3 h-3 text-[#D97904]" />{booking.preferred_date} {booking.preferred_time}</div>}
                  {booking.notes && <p className="mt-2 pt-2 border-t border-[#6B3A1F]/10">{booking.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Photo Manager Tab */}
        {tab === "photos" && (
          <div data-testid="admin-photos">
            <p className="text-sm text-[#6B3A1F] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Manage gallery photos displayed on the website. Upload new project photos or remove existing ones.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {(BUSINESS.gallery || []).map((img, i) => (
                <div key={i} className="relative rounded-xl overflow-hidden border border-[#6B3A1F]/10 aspect-square group">
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#401303]/0 group-hover:bg-[#401303]/50 transition-colors duration-200 flex items-center justify-center">
                    <p className="text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-200 px-2 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>{img.caption}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border-2 border-dashed border-[#6B3A1F]/20 p-8 text-center">
              <Image className="w-10 h-10 text-[#6B3A1F]/30 mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#401303] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Upload New Photos</p>
              <p className="text-xs text-[#6B3A1F] mb-4">Photo upload functionality will be connected to your file storage. For now, contact your web developer to add new project photos.</p>
              <button className="btn-gradient rounded-full px-6 py-2.5 text-sm" data-testid="admin-upload-btn">
                <Image className="w-4 h-4 inline mr-2" /> Upload Photos
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
